// Layout optimization utilities to prevent forced reflows

/**
 * Batch layout reads and writes to avoid forced reflows
 * Usage:
 * batchLayoutOperations(
 *   () => element.offsetWidth,  // read phase
 *   (width) => element.style.width = width + 'px'  // write phase
 * )
 */
export const batchLayoutOperations = <T>(readFn: () => T, writeFn: (result: T) => void) => {
  requestAnimationFrame(() => {
    const result = readFn();
    requestAnimationFrame(() => {
      writeFn(result);
    });
  });
};

/**
 * Cache layout measurements to avoid repeated forced reflows
 */
class LayoutCache {
  cache: Map<string, unknown>;
  rafId: number | null;

  constructor() {
    this.cache = new Map();
    this.rafId = null;

    // Clear cache on resize
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => this.clear(), { passive: true });
    }
  }

  get<T>(key: string, measureFn: () => T): T {
    if (this.cache.has(key)) {
      return this.cache.get(key) as T;
    }

    const value = measureFn();
    this.cache.set(key, value);

    // Auto-clear cache after animation frame
    this.rafId ??= requestAnimationFrame(() => {
      this.rafId = null;
    });

    return value;
  }

  clear() {
    this.cache.clear();
  }
}

export const layoutCache = new LayoutCache();

/**
 * Optimized scroll position getter
 */
export const getScrollPosition = () => {
  return layoutCache.get('scrollY', () => window.scrollY || window.pageYOffset);
};

/**
 * Optimized element dimensions getter
 */
export const getElementDimensions = (element: HTMLElement | null, cacheKey?: string) => {
  if (!element) return null;

  const key = cacheKey ?? `elem-${element.className}`;
  return layoutCache.get(key, () => ({
    width: element.offsetWidth,
    height: element.offsetHeight,
    top: element.offsetTop,
    left: element.offsetLeft,
  }));
};

/**
 * Debounced scroll handler to prevent excessive reflows
 */
export const createOptimizedScrollHandler = (callback: (scrollY: number) => void, _delay = 16) => {
  let rafId: number | null = null;
  let lastScrollY = 0;

  return () => {
    if (rafId) return;

    rafId = requestAnimationFrame(() => {
      const scrollY = window.scrollY || window.pageYOffset;

      // Only call callback if scroll position changed
      if (scrollY !== lastScrollY) {
        callback(scrollY);
        lastScrollY = scrollY;
      }

      rafId = null;
    });
  };
};

/**
 * Optimized resize observer hook
 */
export const createOptimizedResizeObserver = (
  callback: (entries: ResizeObserverEntry[]) => void
) => {
  if (typeof window === 'undefined') {
    return null;
  }

  let rafId: number | null = null;

  return new ResizeObserver(entries => {
    if (rafId) return;

    rafId = requestAnimationFrame(() => {
      callback(entries);
      rafId = null;
    });
  });
};

/**
 * Safe getBoundingClientRect with caching
 */
export const getSafeBoundingRect = (element: HTMLElement | null, cacheKey?: string) => {
  if (!element) return null;

  const key = cacheKey ?? `rect-${element.className}`;
  return layoutCache.get(key, () => element.getBoundingClientRect());
};

/**
 * Batch multiple DOM measurements
 */
export const batchMeasure = <T>(measureFns: (() => T)[]) => {
  return new Promise(resolve => {
    requestAnimationFrame(() => {
      const results = measureFns.map(fn => fn());
      resolve(results);
    });
  });
};

/**
 * Batch multiple DOM writes
 */
export const batchWrite = (writeFns: (() => void)[]) => {
  return new Promise(resolve => {
    requestAnimationFrame(() => {
      for (const fn of writeFns) fn();
      resolve(undefined);
    });
  });
};
